/**
 * Self Improvement Loop — schema (data-driven contract).
 *
 * The internal review workflow of a creative agency: the Art Director sends
 * work back with notes, the Revision Engine addresses those notes and only
 * those notes, and the work is reviewed again — a bounded number of times.
 *
 * This never generates design. It only improves the Creative Plan.
 */

import { PLAN_DECISION_KEYS } from '../thinking';

export const REVISION_SCHEMA_ID = 'revision/1';
export const LOOP_SCHEMA_ID = 'improvement/1';
export const REVISION_VERSION = 1;

/**
 * Decisions the Revision Engine may improve.
 *
 * Everything else is preserved: business intent, marketing intent, the
 * communication goal, the audience and research behind them, the chosen
 * language, and the memory constraints. Re-languaging a design is re-thinking,
 * not revision — that belongs to the Thinking Engine.
 */
export const IMPROVABLE_PLAN_KEYS = Object.freeze([
  'creativeDirection',
  'visualNarrative',
  'compositionStrategy',
  'hierarchyStrategy',
  'typographyStrategy',
  'colorStrategy',
  'photographyStrategy',
  'illustrationStrategy',
  'shapeStrategy',
  'decorationStrategy',
  'ctaStrategy',
  'premiumCues',
  'variationAxes',
]);

/** Everything the engine must leave exactly as the Thinking Engine decided it. */
export const PRESERVED_PLAN_KEYS = Object.freeze(
  PLAN_DECISION_KEYS.filter((key) => !IMPROVABLE_PLAN_KEYS.includes(key)),
);

/** Pattern selection is improvable too, but it is a selection, not a decision. */
export const IMPROVABLE_SELECTION = 'patterns';

/** Why the loop stopped. It always stops for exactly one of these reasons. */
export const STOP_REASONS = Object.freeze({
  APPROVED: 'approved',
  MAX_ITERATIONS: 'max-iterations',
  NO_CONFIDENCE_GAIN: 'no-confidence-gain',
  NO_MEANINGFUL_REVISION: 'no-meaningful-revision',
});

/** Bounded by default: an agency does not review a concept forever. */
export const DEFAULT_LOOP_OPTIONS = Object.freeze({
  maxIterations: 3,
  minConfidenceGain: 0.01,
});

/**
 * How much self-assessed confidence a plan recovers per weakness resolved.
 * A plan that knows its flaws are fixed is more sure of itself, in proportion
 * to how serious those flaws were.
 */
export const CONFIDENCE_RECOVERY = Object.freeze({ blocker: 0.12, major: 0.06, minor: 0.03 });

/** A quiet call to action escalates to one that can carry a campaign. */
export const CTA_ESCALATION = Object.freeze({
  'text-link': 'prominent-solid',
  outline: 'prominent-solid',
  floating: 'prominent-solid',
  'pill-accent': 'prominent-solid',
  'prominent-solid': 'oversized',
});

/** The escalation target when a plan has no call to action at all. */
export const DEFAULT_CTA = 'prominent-solid';
